import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Empty, ErrorBanner, Field, Loading, Modal } from '../../components/ui';
import { api, ApiError } from '../../lib/api';
import { formatDate } from '../../lib/format';
import { useResource } from '../../lib/hooks';
import type {
  AmAuditMatrix as AmAuditMatrixData,
  CsAuditFrequency,
  CsCatalogAudit,
  CsTierKey,
  Employee,
  Me,
} from '../../lib/types';

const TIERS: { key: CsTierKey; label: string; hint: string }[] = [
  { key: 'foundational', label: 'Foundational', hint: 'the checks that must pass anywhere' },
  { key: 'standard', label: 'Standard', hint: 'what a well-run estate looks like' },
  { key: 'advanced', label: 'Advanced', hint: 'the mature end' },
];

const FREQUENCIES: { key: CsAuditFrequency; label: string }[] = [
  { key: 'monthly', label: 'Monthly' },
  { key: 'quarterly', label: 'Quarterly' },
  { key: 'biannual', label: 'Bi-annually' },
  { key: 'annual', label: 'Annually' },
];

/**
 * A client's audit matrix, reached from the button in the client workspace.
 *
 * WHAT THIS IS AND IS NOT. The audits belong to the CS module — that is where
 * they are worked, ticked and edited. This is the account manager's window onto
 * them, plus the one write it makes sense to do from here: standing a matrix up
 * for a client who has none.
 *
 * So the panel has exactly two states, and they are genuinely different jobs:
 *
 *   - The client HAS audits. Show what is being audited and how far each has
 *     got, and link across to CS to actually work them. Read-only here, for the
 *     reason AmCompliance.tsx gives about its own tab: a screen whose purpose is
 *     to read a record should not be where the record quietly changes.
 *
 *   - The client has NONE. Offer to build one. This is the case that matters,
 *     because "no matrix" is the normal state for a client nobody has got to
 *     yet, and the answer to it is a picker rather than a link to an empty list.
 *
 * ACCESS. Reading comes from `/am/clients/:id/audit-matrix`, behind the Account
 * Management page, so any account manager can see it. Building posts to CS and
 * needs the CS page — somebody who cannot open CS should not be creating work
 * inside it. The panel
 * says which of those applies rather than showing a button that will 401.
 */
export function AmAuditMatrix({
  amClientId,
  clientName,
  onClose,
}: {
  amClientId: string;
  clientName: string;
  onClose: () => void;
}) {
  const matrix = useResource<AmAuditMatrixData>(`/am/clients/${amClientId}/audit-matrix`);
  const me = useResource<Me>('/me');

  /* Building posts to /api/cs, which answers to the CS page (migration 144). */
  const canBuild = me.data?.appRole === 'admin' || (me.data?.pages?.includes('cs') ?? false);
  const audits = matrix.data?.audits ?? [];
  const clientId = matrix.data?.clientId ?? null;

  /* Building is offered when there is nothing yet, and stays available
     afterwards through "Add more" — a matrix is rarely right first time. */
  const [building, setBuilding] = useState(false);

  const body = () => {
    if (matrix.error) return <ErrorBanner message={matrix.error} />;
    if (!matrix.data) return <Loading label="Loading the audit matrix" />;

    if (building && canBuild && clientId !== null) {
      return (
        <MatrixBuilder
          clientId={clientId}
          clientName={clientName}
          /* So the picker can grey out what is already there rather than
             silently creating a second copy of the same audit. */
          existingTitles={audits.map((a) => a.Title)}
          onCancel={() => setBuilding(false)}
          onCreated={() => {
            setBuilding(false);
            matrix.reload();
          }}
        />
      );
    }

    if (audits.length === 0) {
      return (
        <Empty title={`No audit matrix defined for ${clientName}`}>
          <p>
            Nothing is being audited for this client yet. A matrix is the set of audits you intend to
            run against their estate — chosen from the CS catalogue, each at the maturity tier you
            are holding them to.
          </p>
          {canBuild ? (
            <button
              type="button"
              className="btn btn-primary mt-4"
              onClick={() => setBuilding(true)}
            >
              Create one
            </button>
          ) : (
            /* A button that 401s is worse than no button — see the note at the
               top of this file, and the same rule in components/Shell.tsx. */
            <p className="muted small mt-4">
              Audits are created in the CS module, which you do not have access to. Ask somebody with
              the CS card to set one up, or request access on your employee record.
            </p>
          )}
        </Empty>
      );
    }

    return (
      <>
        <div className="row-between wrap mb-4">
          <p className="muted small">
            {audits.length} {audits.length === 1 ? 'audit' : 'audits'} against {clientName}. Ticking
            items and editing an audit happen in CS.
          </p>
          {canBuild && (
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => setBuilding(true)}>
              Add more
            </button>
          )}
        </div>

        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Audit</th>
                <th>Cadence</th>
                <th>Owner</th>
                <th className="num">Progress</th>
              </tr>
            </thead>
            <tbody>
              {audits.map((audit) => {
                const done = audit.DoneCount;
                const total = audit.ItemCount;
                return (
                  <tr key={audit.CsAuditId}>
                    <td>
                      <div className="am-cell-title">{audit.Title}</div>
                      <div className="xs muted">Started {formatDate(audit.CreatedUtc)}</div>
                    </td>
                    <td>{FREQUENCIES.find((f) => f.key === audit.Frequency)?.label ?? audit.Frequency}</td>
                    <td>{audit.OwnerName ?? <span className="muted">Unassigned</span>}</td>
                    <td className="num">
                      {/* A fraction, not a percentage: "3 of 14" says how much is
                          left to do, and "21%" does not. */}
                      {total === 0 ? <span className="muted">no checklist</span> : `${done} of ${total}`}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {clientId !== null && canBuild && (
          <p className="mt-4">
            <Link className="btn btn-secondary btn-sm" to={`/cs?client=${clientId}`} onClick={onClose}>
              Open in CS
            </Link>
          </p>
        )}
      </>
    );
  };

  return (
    <Modal title={`Audit matrix — ${clientName}`} onClose={onClose}>
      {body()}
    </Modal>
  );
}

/**
 * Choose what to audit, several at a time, and save once.
 *
 * MULTIPLE BEFORE SAVING is the whole point. Standing a matrix up means picking
 * five or six audits in one sitting, and doing that as five or six saves has a
 * failure mode nobody notices — the third fails, two exist, and the result looks
 * like a deliberate half-built matrix. One request, one transaction; see
 * `POST /cs/audits/matrix`.
 *
 * The tier is per row rather than one for the whole set. A client can genuinely
 * be at Advanced on backup and Foundational on identity, and a single tier across
 * the set would describe a maturity nobody has. "Set all" is there because most
 * of the time they do start at the same tier.
 */
function MatrixBuilder({
  clientId,
  clientName,
  existingTitles,
  onCancel,
  onCreated,
}: {
  clientId: number;
  clientName: string;
  existingTitles: string[];
  onCancel: () => void;
  onCreated: () => void;
}) {
  const catalog = useResource<CsCatalogAudit[]>('/cs/catalog');
  const employees = useResource<Employee[]>('/employees?status=active');

  /* Chosen entries, each with the tier picked for it. A Map so the order the
     catalogue defines is what renders, rather than the order they were ticked. */
  const [picked, setPicked] = useState<Map<number, CsTierKey>>(new Map());
  const [frequency, setFrequency] = useState<CsAuditFrequency>('annual');
  const [ownerEmployeeId, setOwnerEmployeeId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /* Only entries that apply to a client audit. Offering an internal-only entry
     here is the confusion migration 042 exists to remove — and the API refuses
     one anyway, so showing it would only produce a late error. */
  const offered = useMemo(
    () => (catalog.data ?? []).filter((e) => e.Applicability === 'both' || e.Applicability === 'client'),
    [catalog.data],
  );

  /* An audit already on the matrix, matched on the title the batch endpoint
     builds. Not a hard block — running the same audit at a different tier is
     legitimate — but it has to be visible before saving, not after. */
  const alreadyThere = (entry: CsCatalogAudit, tier: CsTierKey) =>
    existingTitles.includes(`${entry.Title} — ${TIERS.find((t) => t.key === tier)?.label ?? ''}`);

  const toggle = (id: number) =>
    setPicked((prev) => {
      const next = new Map(prev);
      if (next.has(id)) next.delete(id);
      else next.set(id, 'foundational');
      return next;
    });

  const setTier = (id: number, tier: CsTierKey) =>
    setPicked((prev) => new Map(prev).set(id, tier));

  const setAllTiers = (tier: CsTierKey) =>
    setPicked((prev) => new Map([...prev.keys()].map((id) => [id, tier])));

  const save = async () => {
    if (picked.size === 0) {
      setError('Choose at least one audit.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await api.post('/cs/audits/matrix', {
        clientId,
        frequency,
        ownerEmployeeId,
        /* Sent in catalogue order, so the audits are created in the order the
           list showed them rather than the order they were ticked. */
        audits: offered
          .filter((e) => picked.has(e.CsCatalogAuditId))
          .map((e) => ({
            catalogAuditId: e.CsCatalogAuditId,
            tier: picked.get(e.CsCatalogAuditId) ?? 'foundational',
          })),
      });
      onCreated();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'The matrix could not be created.');
      setSaving(false);
    }
  };

  if (catalog.error) return <ErrorBanner message={catalog.error} />;
  if (!catalog.data) return <Loading label="Loading the catalog" />;

  if (offered.length === 0) {
    return (
      <Empty title="Nothing in the catalog applies to clients">
        <p>
          Every catalogue entry is marked internal-only, so there is nothing to build a client matrix
          from. Set at least one to &ldquo;Client&rdquo; or &ldquo;Both&rdquo; on the Catalog tab in
          CS.
        </p>
        <button type="button" className="btn btn-ghost mt-4" onClick={onCancel}>
          Back
        </button>
      </Empty>
    );
  }

  return (
    <>
      <p className="muted small">
        Choose what to audit for {clientName}. Each becomes its own audit in CS, with the checklist
        from the tier you pick. You can select several — nothing is saved until you press Create.
      </p>

      {error && <ErrorBanner message={error} />}

      <div className="row-between wrap mt-4 mb-2">
        <strong className="small">
          {picked.size === 0
            ? 'Nothing chosen yet'
            : `${picked.size} of ${offered.length} chosen`}
        </strong>
        {picked.size > 0 && (
          <div className="row wrap">
            <span className="xs muted">Set all to</span>
            {TIERS.map((t) => (
              <button
                key={t.key}
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => setAllTiers(t.key)}
              >
                {t.label}
              </button>
            ))}
          </div>
        )}
      </div>

      <ul className="am-matrix-picker">
        {offered.map((entry) => {
          const chosen = picked.has(entry.CsCatalogAuditId);
          const tier = picked.get(entry.CsCatalogAuditId) ?? 'foundational';
          const itemCount = entry.Tiers[tier]?.items.length ?? 0;
          const duplicate = chosen && alreadyThere(entry, tier);
          return (
            <li key={entry.CsCatalogAuditId} className={chosen ? 'chosen' : undefined}>
              <label className="pick">
                <input
                  type="checkbox"
                  checked={chosen}
                  onChange={() => toggle(entry.CsCatalogAuditId)}
                />
                <span>
                  <span className="am-cell-title">{entry.Title}</span>
                  {entry.Blurb && <span className="xs muted block">{entry.Blurb}</span>}
                </span>
              </label>

              {chosen && (
                <div className="tier-row">
                  <div className="tabs">
                    {TIERS.map((t) => (
                      <button
                        key={t.key}
                        type="button"
                        className={`tab${tier === t.key ? ' active' : ''}`}
                        onClick={() => setTier(entry.CsCatalogAuditId, t.key)}
                        title={t.hint}
                      >
                        {t.label}
                      </button>
                    ))}
                  </div>
                  <span className="xs muted">
                    {/* A tier with no items would create an audit with an empty
                        checklist — worth knowing before saving, not after. */}
                    {itemCount === 0
                      ? 'this tier has no checklist yet'
                      : `${itemCount} ${itemCount === 1 ? 'check' : 'checks'}`}
                  </span>
                  {duplicate && (
                    <span className="xs am-warn">already on the matrix at this tier</span>
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ul>

      <div className="form-grid mt-4">
        <Field label="Cadence" hint="How often each of these should come round again.">
          <select
            className="select"
            value={frequency}
            onChange={(e) => setFrequency(e.target.value as CsAuditFrequency)}
          >
            {FREQUENCIES.map((f) => (
              <option key={f.key} value={f.key}>
                {f.label}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Owner" hint="Applied to every audit created here. Can be changed per audit in CS.">
          <select
            className="select"
            value={ownerEmployeeId ?? ''}
            onChange={(e) => setOwnerEmployeeId(e.target.value ? Number(e.target.value) : null)}
          >
            <option value="">Unassigned</option>
            {(employees.data ?? []).map((emp) => (
              <option key={emp.EmployeeId} value={emp.EmployeeId}>
                {emp.FullName}
              </option>
            ))}
          </select>
        </Field>
      </div>

      <div className="row-between wrap mt-4">
        <button type="button" className="btn btn-ghost" onClick={onCancel} disabled={saving}>
          Cancel
        </button>
        <button type="button" className="btn btn-primary" onClick={() => void save()} disabled={saving}>
          {saving
            ? 'Creating…'
            : picked.size === 0
              ? 'Create'
              : `Create ${picked.size} ${picked.size === 1 ? 'audit' : 'audits'}`}
        </button>
      </div>
    </>
  );
}
